import * as React from "react";

import { cx } from "./controlStyles";

// The one empty state: an icon in a soft 44px tile, a 16px title, one or
// two sentences of help (text or inline <code>), and up to two actions.
//
//   size  page (48px padding, default) | panel (32px) | inline (16px, left-aligned row)
//   tone  error — a load failure: the icon turns danger, the text stays neutral
//
// Say what the place is for and how it fills up ("Routines an agent sets
// up appear here"), never "No data" or "0 rows".

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  size?: "page" | "panel" | "inline";
  tone?: "default" | "error";
  /** Draw the icon on its soft tile (default true). */
  iconFrame?: boolean;
  headingAs?: "h2" | "h3" | "div";
  className?: string;
}

const PAD: Record<NonNullable<EmptyStateProps["size"]>, string> = {
  page: "py-12 px-6",
  panel: "py-8 px-5",
  inline: "py-4 px-4",
};

export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = "page",
  tone = "default",
  iconFrame = true,
  headingAs: Heading = "h3",
  className,
}: EmptyStateProps) {
  const inline = size === "inline";
  const error = tone === "error";
  return (
    <div
      role={error ? "alert" : undefined}
      className={cx(
        "flex",
        inline ? "flex-row items-start gap-3 text-left" : "flex-col items-center text-center",
        PAD[size],
        className,
      )}
    >
      {icon && (
        <div
          aria-hidden
          className={cx(
            "flex shrink-0 items-center justify-center [&>svg]:h-5 [&>svg]:w-5",
            iconFrame && "h-[44px] w-[44px] rounded-lg",
            iconFrame && (error ? "bg-danger/10" : "bg-raised"),
            error ? "text-danger" : "text-fg-muted",
            !inline && "mb-3",
          )}
        >
          {icon}
        </div>
      )}
      <div className={cx("min-w-0", !inline && "flex flex-col items-center")}>
        <Heading className="text-md font-semibold text-fg">{title}</Heading>
        {description && (
          <div className="mt-1 max-w-[48ch] text-base text-fg-muted [&_code]:rounded-sm [&_code]:bg-inset [&_code]:px-1 [&_code]:font-mono [&_code]:text-[0.92em] [&_code]:text-fg">
            {description}
          </div>
        )}
        {(action || secondaryAction) && (
          <div className={cx("mt-5 flex flex-wrap items-center gap-2", !inline && "justify-center")}>
            {action}
            {secondaryAction}
          </div>
        )}
      </div>
    </div>
  );
}
